import React from 'react';
import { createMuiTheme } from '@material-ui/core/styles';
import { fade } from '@material-ui/core/styles/colorManipulator';
import CheckCircleTwoToneIcon from '@material-ui/icons/CheckCircleOutlineTwoTone';

const palette = {
  primary: {
    main: '#3C78B9',
    light: '#F1F8FE',
    hover: '#2E86DE'
  },
  secondary: {
    main: '#01A3A4'
  },
  success: {
    dark3: '#32512D',
    dark2: '#457138',
    dark1: '#579042',
    main: '#6AB04C',
    light1: '#97C882',
    light2: '#D2E7CA',
    light3: '#E9F3E4'
  },
  error: {
    dark3: '#65181F',
    dark2: '#921A21',
    dark1: '#BD1D24',
    main: '#EA2027',
    light: '#FFEDEE',
    light1: '#F16368',
    light2: '#F8BDBF',
    light3: '#FCDEDF'
  },
  info: {
    dark3: '#084C50',
    dark2: '#06696D',
    dark1: '#038688',
    main: '#01A3A4',
    light1: '#4EBFC0',
    light2: '#B3E3E4',
    light3: '#D9F1F1'
  },
  text: {
    primary: '#222F3E',
    secondary: '#26496E'
  },
  neutral: {
    dark3: '#222F3E',
    dark2: '#576574',
    dark1: '#929CA5',
    med: '#B8C6CC',
    light1: '#D0DAE2',
    light2: '#E4EAEF',
    light3: '#F2F5F7',
  }
};

const typography = {
  useNextVariants: true,
  fontFamily: [
    'Poppins',
    'Helvetica',
    'Arial',
    'sans-serif'
  ].join(','),
  fontFamilyInputs: [
    'Work Sans',
    'Segoe UI', 
    'Helvetica', 
    'Tahoma',
    'sans-serif'
  ].join(','),
};

const breakpoints = {
  values: {
    xs: 0,
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1024,
  },
};

const defaultTheme = createMuiTheme({breakpoints});

export default createMuiTheme({
  typography,
  palette,
  breakpoints,
  overrides: {
    MuiCssBaseline: {
      '@global': {
        '.form-section-header': {
          fontSize: 16,
          lineHeight: '24px',
          fontWeight: 600,
          letterSpacing: '.0075em',
          margin: '20px 0 10px 0',
          color: palette.text.secondary,
        },
        '.path-pill': {
          borderRadius: '20px 20px 20px 0px',
          fontSize: 14,
          textTransform: 'uppercase',
          fontWeight: 600,
          padding: '8px 18px',
          margin: '30px 0 10px 0',
          '&--header': {
            borderBottomRightRadius: 0,
            border: '1px solid',
            textAlign: 'center',
          },
          '&--0': {
            marginTop: 0,
            backgroundColor:  palette.info.light3,
            color: palette.info.dark1,
            borderColor: palette.info.light1,
          },
          '&--1': {
            backgroundColor: '#E0ECFF',
            color: '#4979D0',
            borderColor: '#84AFE2'
          },
          '&--2': {
            backgroundColor: '#FFEFEB',
            color: '#EB5515',
            borderColor: '#FCAA84',
          },
        }
      },
    },
    MuiAccordion: {
      root: {
        margin: '8px 0 !important'
      }
    },
    MuiAccordionSummary: {
      root: {
        padding: '11px 15px',
        '&, &.Mui-expanded' : {
          minHeight: 0,
        },
      },
      content: {
        '&, &.Mui-expanded' : {
          margin: 0,
        },
      },
      expandIcon: {
        color: palette.text.primary,
        marginRight: -4,
        padding: 0
      }
    },
    MuiAccordionDetails: {
      root: {
        borderTop: '1px solid ' + palette.neutral.med,
        padding: '20px 24px',
        '& > *': {
          width: '100%',
        }
      }
    },
    // MuiAppBar: {
    //   root: {
    //     height: '75px'
    //   },
    // },
    // MuiBox: {
    //   padding: 35
    // },
    MuiAlert: {
      root: { 
        fontSize: 16,
        fontWeight: 500,
        border: '2px solid',
        borderRadius: 4,
      },
      standardSuccess: {
        borderColor: palette.success.light2,
        color: palette.success.dark3,
        '& .MuiAlert-action': {
          color: palette.success.dark1,
        },
      },
      standardError: {
        borderColor: palette.error.light2,
        color: palette.error.dark2,
        '& .MuiAlert-action': {
          color: palette.error.dark1,
        },
      },
      standardInfo: {
        borderColor: palette.info.light1,
        color: palette.info.dark3,
        '& .MuiAlert-action': {
          color: palette.info.dark1,
        },
        '& .MuiAlert-icon': {
          display: 'none',
        },
      },
    },
    MuiAppBar: {
      colorDefault: {
        backgroundColor: '#FFF'
      },
    },
    MuiButton: {
      root: {
        fontFamily: typography.fontFamilyInputs,
        fontWeight: 600,
        transition: '.3s all',
        '&$disabled': {
          color:  palette.neutral.dark1,
          backgroundColor: palette.neutral.light1,
          borderColor: palette.neutral.light1,
        }
      },
      text: {
        color: palette.neutral.dark2,
        fontSize: 15,
        padding: '6px 16px',
        '&:hover': {
          backgroundColor: palette.neutral.light3,
        },
        '&$disabled': {
          backgroundColor: 'transparent',
        }
      },
      contained: {
        '&$disabled': {
          color:  palette.neutral.dark1,
          backgroundColor: palette.neutral.light1,
          borderColor: palette.neutral.light1,
        }
      },
      containedPrimary: {
        fontSize: 15,
        '&:hover': {
          backgroundColor: palette.primary.hover,
        },
        '&$disabled': {
          color:  palette.neutral.dark1,
          backgroundColor: palette.neutral.light1,
          borderColor: palette.neutral.light1,
        }
      },
      outlinedPrimary: {
        color: palette.primary.main,
        border: '2px solid currentColor',
        fontSize: 15,
        backgroundColor: '#fff',
        transition: 'all .3s',
        '&:hover': {
          color: palette.primary.hover,
          backgroundColor:  palette.primary.light,
          border: '2px solid currentColor',
        },
        '&$disabled': {
          border: '2px solid ' + palette.neutral.light1,
        }
      },
      textPrimary: {
        minWidth: 0,
        '&:hover': {
          backgroundColor: 'transparent',
          color: palette.primary.hover,
        },
      },
    },
    MuiCheckbox: {
      root: {
        '& .MuiSvgIcon-root': {
          fontSize: 19,
        },
        '& ~ .MuiFormControlLabel-label': {
          fontSize: 16,
          fontFamily: typography.fontFamilyInputs,
          fontWeight: 600,
          color: palette.neutral.dark2,
        }
      },
      colorPrimary: {
        color: palette.neutral.dark1,
        '&$checked': {
          color: palette.primary.hover,
        },
      }
    },
    MuiDialog: {
      root: {
        '& .MuiPaper-rounded': {
          borderRadius: 8,
          boxShadow: `0px 0px 25px ${fade('#000',.35)}`,
          width: '100%'
        },
      },
    },
    MuiDialogTitle: {
      root: {
        padding: '30px 35px',
        fontSize: 20,
        '& .MuiTypography-root': {
          fontWeight: 600,
          letterSpacing: '.01em'
        },
      }
    },
    MuiDialogContent: {
      root: {
        padding: '0px 35px',
      }, 
    },
    MuiDialogContentText: {
      root: {
        marginBottom: 25,
        '& .MuiTypography-root': {
          color: palette.neutral.dark2
        },
      }
    },
    MuiDialogActions: {
      root: {
        padding: '10px 35px 30px 35px',
        // '& . MuiButton-text': {
        // },
        '& .MuiButton-containedPrimary': {
          color: palette.primary.main,
          backgroundColor: palette.neutral.light3,
          '&:hover': {
            textDecoration: 'none',
            backgroundColor: palette.primary.light,
          }
        },
      },
    },
    MuiBackdrop: {
      root: {
        backgroundColor: fade('#000',.35),
      },
    },
    MuiDivider: {
      root: {
        backgroundColor: palette.neutral.med,
        margin: '30px 0px',
        width: '100%',
        // '& + *' : {
        //   marginTop: 0,
        // },
      },
    },
    MuiFab: {
      root: {
        boxShadow: 'none',
        '&$disabled' : {
          backgroundColor:  palette.neutral.light1,
          color: palette.neutral.dark1,
        }
      },
      primary: {
        fontWeight: 600,
        fontSize: 15,
        '&.MuiLink-root:hover, &:hover': {
          backgroundColor: palette.primary.hover,
          textDecoration: 'none',
          color: '#fff',
        },
      },
      sizeMedium: {
        height: 45,
        width: 45,
        fontSize: 14,
      },
    },
    MuiInputBase: {
      root: {
        background: '#fff',
        '& legend': {
          width: 0,
        },
      },
      input: {
        fontFamily: typography.fontFamilyInputs,
        background: '#fff',
        borderRadius: 4,
        fontWeight: 500,
        '&::placeholder': {
          fontStyle: 'italic'
        }
      },
    },
    MuiInput: {
      root: {
        background: '#fff',
        color: palette.neutral.dark2,
      },
    },
    MuiInputLabel: {
      outlined: {
        transform: 'translate(14px, 33px) scale(1)',
        '&.MuiInputLabel-shrink': {
          transform: 'translate(0px, 0px) scale(0.75)'
        },
      },
      root: {
        fontFamily: typography.fontFamilyInputs,
        '&.Mui-focused': {
          color: palette.primary.hover,
        },
      },
    },
    MuiOutlinedInput: {
      root: {
        marginTop: 20,
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: palette.primary.hover
        },
      },
      input: {
        color: palette.neutral.dark2,
        padding: '10.5px 14px',
        '&::placeholder': {
          opacity: 1
        },
      },
      multiline: {
        padding: 14,
      }
    },
    MuiFormControl: {
      root: {
        // margin: 10,
        // minWidth: 225,
        // width: '100%',
        // maxWidth: 415,
        color: '#707070'
      },
      fullWidth: {
        // maxWidth: 'none'
      }
    },
    MuiFormHelperText: {
      root: {
        color: palette.text.primary,
        fontFamily: typography.fontFamilyInputs,
        lineHeight: '16px',
        '& a': {
          textDecoration: 'underline'
        }
      }
    },
    MuiFormLabel: {
      root: {
        fontFamily: typography.fontFamilyInputs,
        color: palette.neutral.dark2,
        fontWeight: 500,
      },
    },
    MuiLink: {
      root: {
        fontWeight: 600,
        transition: '.3s color',
        '&:hover': {
          color: palette.primary.hover
        },
      },
    },
    MUIRichTextEditor: {
      root: {
        borderRadius: 4,
        background: '#fff',
        border: '1px solid currentColor', // allows it to change based on parent w/ error styling
      },
      toolbar: {
        borderBottom: '1px solid ' + palette.neutral.med,
      },
      editorContainer: {
        padding: '0px 12px',
        margin: '16px 0px',
        minHeight: 48,
        fontWeight: 500,
        color: palette.neutral.dark2,
      },
      placeHolder: {
        position: 'relative',
        fontStyle: 'italic',
      },

    },
    MuiStepper: {
      root: {
        background: 'none',
        padding: 0,
        margin: '0 -6px 40px -6px'
      }
    },
    MuiStepButton: {
      root: {
        color: palette.primary.main,
        margin: '-24px -12px',
        padding: '24px 12px',
        '&:hover': {
          '& .MuiStepLabel-label, & .MuiStepLabel-label.MuiStepLabel-active': {
            color: palette.primary.hover,
          },
          '& .MuiStepLabel-iconContainer': {
            color: palette.primary.hover,
            backgroundColor: palette.primary.light,
          }
        },
      },
    },
    MuiStepLabel: {
      label: {
        position: 'relative',
        display: 'block',
        fontWeight: 600,
        color: 'inherit',
        transition: 'color .3s',
        '&$active': {
          color: palette.text.primary,
          background: '#fff',
          padding: '5px 10px',
          borderRadius: 20,
          margin: '11px -10px 0 !important',
        }
      },
      iconContainer: {
        border: '2px solid currentColor',
        backgroundColor: '#fff',
        borderRadius: '50%',
        color: palette.primary.main,
        height: 26,
        width: 26,
        alignItems: 'center',
        boxSizing: 'border-box',
        justifyContent: 'center',
        transition: 'background-color .3s'
      },
    },
    MuiTabs: {
      root: {
        paddingRight: 32,
        paddingLeft: 32,
        '& .MuiSvgIcon-root': {
          height: 52,
          width: 52,
        },
      },
      indicator: {
        height: 3,
        color: palette.primary.hover
      },
    },
    MuiTab: {
      root: {
        fontSize: 35,
        padding: '10px 12px',
        width: '33%',
        maxWidth: 200,
        [defaultTheme.breakpoints.up('sm')]: {
          minWidth: 100,
        },
      },
      textColorPrimary: {
        color: palette.primary.main,
        '&:hover': {
          backgroundColor: palette.primary.light,
          color: palette.primary.hover,
        },
        '&$selected': {
          color: palette.text.primary,
        },
      },
    },
    MuiTable: {
      root: {
        borderCollapse: 'separate',
        borderSpacing: '0 8px'
      },
    },
    MuiTableCell: {
      root: {
        background: '#fff',
        borderBottom: 'none',
        padding: '11px 16px',
        '&:first-child': {
          borderTopLeftRadius: 8,
          borderBottomLeftRadius: 8,
        },
        '&:last-child': {
          borderTopRightRadius: 8,
          borderBottomRightRadius: 8,
        },
        maxWidth: '300px'
      },
      head: {
        backgroundColor: 'transparent'
      },
      footer: {
        maxWidth: 'none',
        width: 'auto !important',
        marginLeft: 'auto',
      },
    },
    MuiTablePagination: {
      spacer: {
        display: 'none'
      },
      root: {
        '& .MuiButton-root': {
          // color: palette.primary.main,
          margin: '0 2px',
          fontSize: 14,
          fontWeight: 500,
          backgroundColor: '#fff',
          transition: 'all .3s',
          '&.Mui-disabled': {
            border: '1px solid currentColor',
            borderRadius: 2,
            color: palette.primary.hover,
          },
          '&:hover': {
            color: palette.primary.hover,
            backgroundColor:  palette.primary.light,
            // border: '2px solid currentColor',
          },
        },
        '& .MuiIconButton-root:not(.Mui-disabled)': {
          color: palette.primary.main,
          padding: '4px 5px',
          margin: 8,
          borderRadius: 2,
          '&:hover': {
            color: palette.primary.hover,
            background: palette.primary.light,
          },
        },
      },
      toolbar: {
        padding: '0 20px',
      },
    },
    MuiTableRow: {
      footer: {
        display: 'flex',
        width: 'auto'
      },
    },
    MuiTableSortLabel: {
      root: {
        '&:not(.MuiTableSortLabel-active):hover': {
          color: palette.primary.hover,
          '& .MuiTableSortLabel-icon': {
            opacity: 1,
            '& svg': {
              color: palette.primary.hover,
            },
          },
        },
      },
      active: {
        '&:hover, &.MuiTableSortLabel-active:hover': {
          color: palette.primary.hover,
          '& .MuiTableSortLabel-icon': {
            opacity: 1,
            color: palette.primary.hover + ' !important',
          },
        },
      },
      icon: {
        transition: 'none',
      },
      iconDirectionDesc: {
       transform: 'rotate(180deg) translate(-20px)'
      },
      iconDirectionAsc: {
        transform: 'translate(0px)'
       },
    },
    MuiTooltip: {
      arrow: {
        color: palette.neutral.dark2,
      },
      tooltip: {
        fontSize: 14,
        fontWeight: 500,
        backgroundColor: palette.neutral.dark2,
        padding: 12,
      },
    },
    MuiToolbar: {
      regular: {
        [defaultTheme.breakpoints.up('sm')]: {
          minHeight: 75,
        },
      },
      dense: {
        [defaultTheme.breakpoints.up('sm')]: {
          minHeight: 56
        },
      },
    },
    MuiTypography: {
      h4: {
        fontSize: '1.6rem',
        [defaultTheme.breakpoints.up('lg')]: {
          fontSize: '2.1rem'
        }
      }
    }
  },
  props: {
    MuiAlert: {
      iconMapping: {
        success: <CheckCircleTwoToneIcon />,
      }
    },
    MuiAppBar: {
      color: 'default',
    },
    MuiButton: {
      variant: 'contained',
      disableElevation: true
    },
    MuiFormHelperText: {
      variant: 'filled'
    },
    MuiTextField: {
      variant: 'outlined',
    },
    MuiFab: {
      size: 'medium',
    },
    MuiLink: {
      color: 'primary',
    },
    MuiPaginationItem: {
      variant: 'outlined',
    },
    MuiSnackbar: {
      anchorOrigin: { 
        vertical: 'top', 
        horizontal: 'center' 
      },
    },
    MuiTab: {
      disableRipple: true,
    },
    MuiTooltip: {
      arrow: true,
    },
  },
});